import React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';

import Dialog from 'src/components/dialog/Dialog';
import { useProperty } from 'src/services/api/properties/use-property';
import { EFeatureFlag, useIsFeatureEnabled } from 'src/utils/feature-flags';

const ELISE_AI_PROVIDER = 'elise_ai';

type Props = {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void | Promise<void>;
    isCanceling: boolean;
    /** The tour's property, used to check whether it's Elise-integrated. */
    propertyId?: number | null;
};

export default function CancelTourConfirmModal({ open, onClose, onConfirm, isCanceling, propertyId }: Props) {
    const isEliseIntegrationEnabled = useIsFeatureEnabled(EFeatureFlag.ELISE_AI_INTEGRATION);
    const { data: property } = useProperty(
        isEliseIntegrationEnabled && open ? propertyId : null,
        { include_integrations: true },
    );
    const isElise = isEliseIntegrationEnabled &&
        !!property?.integrations?.integrations.some((i) => i.provider === ELISE_AI_PROVIDER);

    return (
        <Dialog
            open={open}
            onClose={() => {
                if (!isCanceling) {
                    onClose();
                }
            }}
            maxWidth="xs"
            fullWidth
            aria-labelledby="cancel-tour-dialog-title"
        >
            <DialogTitle id="cancel-tour-dialog-title">Cancel tour?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to cancel this tour? This cannot be undone.
                </DialogContentText>
                {isElise && (
                    <Alert severity="warning" data-testid="elise-cancel-tour-alert" sx={{ mt: 2 }}>
                        Canceling here does not cancel the tour on the property&apos;s website.
                        Please contact the onsite team to cancel the tour as well.
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isCanceling}
                    data-testid="cancel-tour-confirm-modal-dismiss-button"
                >
                    Keep tour
                </Button>
                <LoadingButton
                    variant="contained"
                    color="error"
                    onClick={() => void onConfirm()}
                    loading={isCanceling}
                    data-testid="cancel-tour-confirm-modal-confirm-button"
                >
                    Cancel tour
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
